import { Link } from "react-router-dom";
import { Fade, MaskedLines, Reveal } from "@/components/motion";
import { SITE } from "@/lib/site";

const CONTENT = {
    terms: {
        eyebrow: "Legal",
        title: "Terms & Conditions",
        updated: "Last updated: July 2026",
        sections: [
            {
                h: "1. Who we are",
                paras: [
                    `Sthaan (sthaan.space) is an online discovery and connection platform operated by ${SITE.company} (CIN: ${SITE.cin}), with its registered office at ${SITE.address}. By using this website you accept these terms.`,
                ],
            },
            {
                h: "2. What Sthaan does — and does not do",
                paras: [
                    "Sthaan showcases newly launched, under-construction and ready-to-move residential and commercial projects, and helps interested visitors contact the respective developers directly.",
                    "Sthaan is not a real-estate brokerage firm, broker, agent, developer or seller. Sthaan does not charge buyers brokerage or fees, does not negotiate, mediate or execute property transactions, does not hold or process payments, and does not act on behalf of any developer or buyer in any transaction.",
                ],
            },
            {
                h: "3. Project information",
                paras: [
                    "Project information shown on Sthaan — including pricing, availability, configurations, areas, specifications, amenities, approvals, RERA details and possession timelines — is provided by the respective developers or their representatives.",
                    "Sthaan may present developer-supplied information in a structured and visually enhanced format. Unless expressly stated, this presentation does not mean Sthaan has independently verified or guaranteed that information. You should independently verify all important details directly with the developer, including RERA registration on the official state RERA portal, before making any decision.",
                ],
            },
            {
                h: "4. Contacting developers directly",
                paras: [
                    "When you choose to contact a developer through WhatsApp or another direct communication mechanism provided on Sthaan, you are voluntarily initiating communication with that developer. The developer may receive the information necessary to respond (such as your phone number, through WhatsApp) and may contact you directly regarding your enquiry.",
                    "By choosing to contact a developer through Sthaan, you understand that your communication may be shared directly with the respective developer and that the developer may contact you regarding your enquiry. Your use of WhatsApp or any third-party service is governed by that service's own terms and privacy policy.",
                ],
            },
            {
                h: "5. No accounts, no obligation",
                paras: [
                    "Sthaan does not require you to create an account to browse projects or contact developers. Browsing the website does not create any obligation to transact.",
                ],
            },
            {
                h: "6. No advice",
                paras: [
                    "Nothing on Sthaan constitutes legal, financial, investment, tax or property advice. Any decision to purchase or invest in a property is yours alone; please consult qualified professionals where appropriate.",
                ],
            },
            {
                h: "7. Intellectual property",
                paras: [
                    "The Sthaan name, logo, design and original content are the property of the platform operator. Project imagery, brochures and related material remain the property of the respective developers or their licensors and are displayed with permission.",
                ],
            },
            {
                h: "8. Limitation of liability",
                paras: [
                    "To the fullest extent permitted by applicable law, Sthaan and its operator are not responsible for disputes, transactions, representations, commitments, delays, defects, misstatements, changes in pricing or availability, construction issues, or any other matters arising between users and developers.",
                    "Sthaan does not guarantee any property's availability, price, quality, legality, approvals, possession, construction progress, or any representations made by developers.",
                ],
            },
            {
                h: "9. Changes and governing law",
                paras: [
                    "We may update these terms from time to time; the version on this page applies. These terms are governed by the laws of India, and courts at Gandhidham, Gujarat shall have jurisdiction, subject to applicable law.",
                ],
            },
            {
                h: "10. Contact",
                paras: [`Questions about these terms: ${SITE.email}.`],
            },
        ],
    },
    privacy: {
        eyebrow: "Legal",
        title: "Privacy Policy",
        updated: "Last updated: July 2026",
        sections: [
            {
                h: "1. Our approach",
                paras: [
                    "Sthaan is designed to be browsed freely. We do not require user accounts, we do not run a lead database, and we do not intentionally collect your personal information for marketing or resale.",
                ],
            },
            {
                h: "2. What may be processed automatically",
                paras: [
                    "Like most websites, some technical data may be processed automatically when you visit: standard server/hosting logs (such as IP address, browser and device type, pages visited and timestamps) used for security, performance and abuse prevention; and privacy-respecting analytics that help us understand aggregate usage.",
                    "To make discovery locally relevant, your approximate city may be determined from your IP address using a geolocation service. This happens in your browser, is kept only in your own browser's session storage, and is not sent to or stored in a Sthaan database.",
                ],
            },
            {
                h: "3. When you contact a developer",
                paras: [
                    "If you tap a WhatsApp or similar direct-contact button, you leave Sthaan and initiate communication with the respective developer through that third-party service. The developer may receive the information necessary to respond (for example your WhatsApp number) and may contact you directly.",
                    "By choosing to contact a developer through Sthaan, you understand that your communication may be shared directly with the respective developer and that the developer may contact you regarding your enquiry. WhatsApp and similar services process your data under their own privacy policies.",
                ],
            },
            {
                h: "4. Cookies and storage",
                paras: [
                    "Sthaan itself uses minimal browser storage (for example, to remember your approximate city during a session). Third-party services embedded in the site (such as hosting, security or analytics providers) may set their own cookies as described in their policies.",
                ],
            },
            {
                h: "5. Data sharing",
                paras: [
                    "We do not sell personal data. Technical data may be processed by our hosting, security and analytics providers strictly to operate and protect the website.",
                ],
            },
            {
                h: "6. Your choices and rights",
                paras: [
                    "You can clear your browser storage at any time to remove locally stored preferences. For any privacy question, request or concern, contact us and we will respond.",
                ],
            },
            {
                h: "7. Contact",
                paras: [`Privacy questions: ${SITE.email}.`],
            },
        ],
    },
    disclaimer: {
        eyebrow: "Legal",
        title: "Disclaimer",
        updated: "Last updated: July 2026",
        sections: [
            {
                h: "1. Platform, not participant",
                paras: [
                    "Sthaan is a technology and discovery platform. It is not a real-estate broker, agent, developer, seller or transaction intermediary, and it charges buyers no brokerage or fees.",
                ],
            },
            {
                h: "2. Developer-supplied information",
                paras: [
                    "All project information on Sthaan is sourced from the respective developers or their representatives. Sthaan may structure and visually enhance this material, but unless expressly stated does not independently verify or guarantee it.",
                    "Please independently verify pricing, availability, specifications, areas, amenities, approvals, RERA registration, possession dates and all other project details directly with the developer — including on the official state RERA portal — before making any decision.",
                ],
            },
            {
                h: "3. Imagery",
                paras: [
                    "Images, renders, plans and films may be artistic impressions, concept visualisations or representative imagery supplied by developers. Actual projects may differ.",
                ],
            },
            {
                h: "4. Concept demonstrations",
                paras: [
                    "Certain pages on Sthaan (such as the private /demo concept showcase) are fictional demonstrations created to illustrate the platform's presentation capabilities. They are clearly labelled, and do not represent real properties, developers, prices or approvals.",
                ],
            },
            {
                h: "5. No responsibility for transactions",
                paras: [
                    "To the extent permitted by applicable law, Sthaan is not responsible for disputes, transactions, representations, commitments, delays, defects, misstatements, changes in pricing or availability, construction issues, or other matters arising between users and developers. Nothing on Sthaan is legal, financial, investment or property advice.",
                ],
            },
            {
                h: "6. Contact",
                paras: [`Questions about this disclaimer: ${SITE.email}.`],
            },
        ],
    },
};

const TABS = [
    { kind: "terms", label: "Terms", to: "/terms", id: "legal-tab-terms" },
    { kind: "privacy", label: "Privacy", to: "/privacy", id: "legal-tab-privacy" },
    { kind: "disclaimer", label: "Disclaimer", to: "/disclaimer", id: "legal-tab-disclaimer" },
];

export default function Legal({ kind }) {
    const doc = CONTENT[kind] || CONTENT.terms;
    return (
        <div data-testid={`legal-${kind}-page`} className="mx-auto max-w-4xl px-5 pb-24 pt-40 sm:px-8">
            <Fade delay={0.1}>
                <p className="eyebrow">{doc.eyebrow}</p>
            </Fade>
            <h1 className="mt-4 font-serif text-4xl font-light tracking-tight text-ivory sm:text-5xl">
                <MaskedLines lines={[doc.title]} />
            </h1>
            <Fade delay={0.5}>
                <p className="mt-3 font-mono text-xs text-dim">{doc.updated}</p>
            </Fade>

            <Fade delay={0.6}>
                <div className="mt-8 flex flex-wrap gap-2" data-testid="legal-tabs">
                    {TABS.map((t) => (
                        <Link
                            key={t.kind}
                            to={t.to}
                            data-testid={t.id}
                            className={`rounded-full border px-5 py-2 text-sm transition-colors ${
                                t.kind === kind
                                    ? "border-gold bg-gold text-obsidian"
                                    : "border-black/10 text-mist hover:border-gold/40 hover:text-ivory"
                            }`}
                        >
                            {t.label}
                        </Link>
                    ))}
                </div>
            </Fade>

            <div className="mt-12 space-y-10">
                {doc.sections.map((s) => (
                    <Reveal key={s.h}>
                        <section>
                            <h2 className="font-serif text-2xl text-gold">{s.h}</h2>
                            {s.paras.map((p, i) => (
                                <p key={i} className="mt-3 text-sm leading-relaxed text-mist">
                                    {p}
                                </p>
                            ))}
                        </section>
                    </Reveal>
                ))}
            </div>
        </div>
    );
}
